/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";

import { useUnityDispatch, useUnitySelector } from "../../viewModel";
import {
  createDirectionalCodeInterceptor,
  InputAction
} from "../../viewModel/navigation";
import type { NavigationEngine } from "../../viewModel/navigation";
import {
  DebugOverlayMode,
  copyVideoDebugMetrics,
  recordLastKeyCode,
  selectIsVideoDebugOverlayVisible,
  toggleVideoDebugOverlay
} from "../../viewModel/videoDebugOverlay";

/**
 * The remote gesture that toggles the overlay: UP UP DOWN DOWN RIGHT, as
 * specified in the Video Debug Overlay proposal §4.
 */
const TOGGLE_SEQUENCE: ReadonlyArray<InputAction> = [
  InputAction.NAV_UP,
  InputAction.NAV_UP,
  InputAction.NAV_DOWN,
  InputAction.NAV_DOWN,
  InputAction.NAV_RIGHT
];

/**
 * The remote gesture that copies the current stats, available only while
 * the overlay is on screen. Deliberately the toggle gesture with its last
 * press mirrored: one shape to remember, and a remote has no spare button
 * that is mapped on every platform this app runs on.
 */
const COPY_SEQUENCE: ReadonlyArray<InputAction> = [
  InputAction.NAV_UP,
  InputAction.NAV_UP,
  InputAction.NAV_DOWN,
  InputAction.NAV_DOWN,
  InputAction.NAV_LEFT
];

const TOGGLE_INTERCEPTOR_NAME: string = "videoDebugOverlayToggle";
const COPY_INTERCEPTOR_NAME: string = "videoDebugOverlayCopy";

/** `KeyboardEvent.code` for the toggle hotkey's letter (Ctrl+Shift+D). */
const TOGGLE_HOTKEY_CODE: string = "KeyD";

/**
 * `KeyboardEvent.code` for the copy hotkey's letter (Ctrl+Shift+X).
 * X rather than the obvious C: Ctrl+Shift+C opens the element inspector in
 * every Chromium browser, which is precisely the audience this shortcut is
 * for.
 */
const COPY_HOTKEY_CODE: string = "KeyX";

/**
 * Installs the transient triggers for the Video Debug Overlay.
 *
 * - **Toggle** — UP UP DOWN DOWN RIGHT on a remote, Ctrl+Shift+D on
 *   desktop. Both open the overlay in **expanded** mode, and both close
 *   it again when expanded is what is already showing. (The "Stream
 *   Stats" item in the playback action bar owns compact mode; pressing
 *   this while compact is showing switches to expanded rather than
 *   closing.)
 * - **Copy stats** — UP UP DOWN DOWN LEFT, or Ctrl+Shift+X. Registered
 *   only while the overlay is visible, so the gesture is inert during
 *   ordinary use and cannot consume a navigation key it should not.
 *
 * Unlike the other debug triggers in this app, these are installed in
 * **every build**. The overlay is a field-support tool as much as a QA
 * one — the case it exists for is diagnosing a customer's playback on
 * their own device, where getting a debug build installed first is not an
 * option. What a release build withholds is the *restricted* metrics
 * (stream URL, CDN node, device id, key statuses), and that gating lives
 * in `applyMetricsPrivacyFilter`, not here.
 *
 * ## Why the hotkeys are plain listeners and not interceptors
 *
 * `NavigationEngine` drops any key its platform mapper resolves to
 * `UNKNOWN` before interceptors run, and on the browser map "d" happens to
 * resolve to `COLOR_BLUE` — so routing a letter hotkey through the
 * pipeline would make it depend on a mapping that exists for the colour
 * buttons and could reasonably change. A `capture`-phase listener on
 * `document` is independent of all of it, and matching on `event.code`
 * keeps the shortcut working on a non-QWERTY layout.
 *
 * @param engine - The NavigationEngine instance from context.
 */
export function useVideoDebugOverlayTrigger(engine: NavigationEngine): void {
  const dispatch = useUnityDispatch();
  const isVisible: boolean = useUnitySelector(selectIsVideoDebugOverlayVisible);

  // ── Toggle: always installed ──
  useEffect((): (() => void) => {
    const interceptor = createDirectionalCodeInterceptor(
      TOGGLE_SEQUENCE,
      (): void => {
        dispatch(toggleVideoDebugOverlay(DebugOverlayMode.EXPANDED));
      }
    );
    engine.getInterceptors().use(TOGGLE_INTERCEPTOR_NAME, interceptor);

    const handleHotkey = (event: KeyboardEvent): void => {
      // Every key goes past here, so this is where the overlay's "Last
      // Key" row is fed. Deliberately before the hotkey guards: an
      // unmapped key is the one a key-map bug is about, and the
      // navigation engine drops those before any interceptor sees them.
      recordLastKeyCode(event);

      if (!event.ctrlKey || !event.shiftKey || event.altKey) {
        return;
      }
      if (event.code !== TOGGLE_HOTKEY_CODE) {
        return;
      }
      event.preventDefault();
      dispatch(toggleVideoDebugOverlay(DebugOverlayMode.EXPANDED));
    };

    document.addEventListener("keydown", handleHotkey, true);

    return (): void => {
      engine.getInterceptors().remove(TOGGLE_INTERCEPTOR_NAME);
      document.removeEventListener("keydown", handleHotkey, true);
    };
  }, [engine, dispatch]);

  // ── Copy stats: installed only while the overlay is on screen ──
  useEffect((): (() => void) | undefined => {
    if (!isVisible) {
      return undefined;
    }

    const interceptor = createDirectionalCodeInterceptor(
      COPY_SEQUENCE,
      (): void => {
        dispatch(copyVideoDebugMetrics());
      }
    );
    engine.getInterceptors().use(COPY_INTERCEPTOR_NAME, interceptor);

    const handleHotkey = (event: KeyboardEvent): void => {
      if (!event.ctrlKey || !event.shiftKey || event.altKey) {
        return;
      }
      if (event.code !== COPY_HOTKEY_CODE) {
        return;
      }
      event.preventDefault();
      dispatch(copyVideoDebugMetrics());
    };

    document.addEventListener("keydown", handleHotkey, true);

    return (): void => {
      engine.getInterceptors().remove(COPY_INTERCEPTOR_NAME);
      document.removeEventListener("keydown", handleHotkey, true);
    };
  }, [engine, dispatch, isVisible]);
}
